import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/layout/PageHeader';
import Table from '../../components/ui/Table';
import Tabs from '../../components/ui/Tabs';
import Input from '../../components/ui/Input';
import CandidateFilterBar, { useCandidateFilters } from '../../components/filters/CandidateFilterBar';
import { StatusBadge } from '../../components/ui/Badge';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { listOtherCandidates, revertCandidateStatus } from '../../api/candidates';
import { pushToast } from '../../components/ui/toastBus';
import SearchSummary, { withMatchColumn } from '../../components/ui/SearchMatch';

const TAB_LABELS = { suspended: 'Suspended', hold: 'Hold', withdrawn: 'Backout' };
const REVERT_LABELS = { suspended: 'Unsuspend', hold: 'Unhold', withdrawn: 'Unbackout' };

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString() : '—');

export default function OtherCandidates() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const filters = useCandidateFilters();
  useEffect(() => setPage(1), [filters.params]);
  const [revertTarget, setRevertTarget] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const params = { ...filters.params, status: status || undefined, search: search || undefined, page };
  const query = useQuery({
    queryKey: ['other-candidates', params],
    queryFn: () => listOtherCandidates(params),
    placeholderData: (prev) => prev,
  });

  // Until the first response arrives we don't know which tabs this user may see; the server
  // defaults to the first permitted one, so adopt it as the active tab.
  const tabs = useMemo(() => query.data?.tabs || [], [query.data]);
  useEffect(() => {
    if (!status && tabs.length) setStatus(tabs[0].status);
  }, [status, tabs]);

  const revertMutation = useMutation({
    mutationFn: (id) => revertCandidateStatus(id),
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Candidate status reverted.' });
      queryClient.invalidateQueries({ queryKey: ['other-candidates'] });
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      setRevertTarget(null);
    },
    onError: (err) => {
      pushToast({ type: 'error', message: err.response?.data?.message || 'Could not revert.' });
      setRevertTarget(null);
    },
  });

  const columns = [
    {
      key: 'name',
      label: 'Candidate',
      render: (row) => (
        <div>
          <Link to={`/candidates/${row.id}`} className="font-medium text-primary hover:underline">
            {row.full_name}
          </Link>
          <div className="text-xs text-gray-400">{row.reference_no}</div>
        </div>
      ),
    },
    { key: 'contact_no', label: 'Contact' },
    { key: 'category', label: 'Category' },
    { key: 'current_step', label: 'Step', render: (row) => row.current_step || '—' },
    { key: 'status', label: 'Status', render: (row) => <StatusBadge status={row.status} /> },
    { key: 'reason', label: 'Reason', render: (row) => <span className="text-gray-600">{row.reason || '—'}</span> },
    { key: 'updated_at', label: 'Since', render: (row) => fmtDate(row.updated_at) },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <button onClick={() => setRevertTarget(row)} className="text-sm text-primary hover:underline">
          {REVERT_LABELS[row.status]}
        </button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="Other Candidates" description="Suspended, on-hold and backout candidates." />

      <Tabs
        tabs={tabs.map((t) => ({ value: t.status, label: `${TAB_LABELS[t.status]} (${t.count})` }))}
        active={status}
        onChange={(v) => {
          setStatus(v);
          setPage(1);
        }}
      />

      <div className="my-4">
        <CandidateFilterBar {...filters.barProps} className="">
          <Input placeholder="Search name, reference or contact" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} className="w-72" />
        </CandidateFilterBar>
      </div>

      <SearchSummary search={search} total={query.data?.candidates?.total} rows={query.data?.candidates?.data || []} paged />

      <Table
        columns={withMatchColumn(columns, search)}
        data={query.data?.candidates?.data || []}
        isLoading={query.isLoading}
        pagination={query.data?.candidates}
        onPageChange={setPage}
        emptyMessage="No candidates in this list."
      />

      <ConfirmDialog
        open={!!revertTarget}
        onClose={() => setRevertTarget(null)}
        onConfirm={() => revertMutation.mutate(revertTarget.id)}
        title={revertTarget ? REVERT_LABELS[revertTarget.status] : ''}
        message={`Restore ${revertTarget?.full_name} (${revertTarget?.reference_no}) to the active pipeline?`}
        confirmLabel="Confirm"
        loading={revertMutation.isPending}
      />
    </div>
  );
}
